import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useBrand } from "../theme/ThemeProvider";
import { IntervalToggle } from "../components/IntervalToggle";
import {
  api,
  type BillingState,
  type Entitlement,
  type PlanCatalogEntry,
} from "../lib/api";
import {
  groupPlansByInterval,
  intervalSavingsLabel,
  isLocked,
  planFeatures,
  planPriceParts,
  rowForInterval,
} from "../lib/plans";
import { daysLeft } from "../lib/time";
import { useBillingPoll } from "../lib/useBillingPoll";

// Billing runs through Lemon Squeezy as merchant-of-record (global VAT
// / sales tax handled). The plan catalog is server-side data
// (billing_plan), so tiers are retunable without an app deploy; the
// entitlement lands via the LS webhook after payment.
export function Billing() {
  const brand = useBrand();
  const [searchParams] = useSearchParams();
  const checkoutReturn = searchParams.get("checkout") === "return";

  const [state, setState] = useState<BillingState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<"month" | "year">(
    "month",
  );

  // One fetch-and-store shape shared by the mount effect and the poll's
  // terminal fallback below, so error handling can't drift between them.
  function refreshBillingState() {
    api
      .billing()
      .then(setState)
      .catch((e) => setErr(String((e as Error).message)));
  }

  useEffect(() => {
    // Mirrors Welcome.tsx: on the checkout-return leg, useBillingPoll below
    // owns the fetch (it needs to poll until the LS webhook lands) — an
    // unconditional fetch here would race it with a duplicate request.
    if (checkoutReturn) return;
    refreshBillingState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutReturn]);

  // Only actually polls when the URL carries ?checkout=return (a plan
  // switch just came back from Lemon Squeezy); otherwise idle. Its
  // freshest read wins over the plain mount-time fetch above once
  // available, so the entitlement/plan list update as soon as the LS
  // webhook lands without a manual refresh.
  const poll = useBillingPoll(checkoutReturn);
  const effectiveState = poll.state ?? state;

  // Terminal fallback: useBillingPoll swallows its own errors by design
  // (it just keeps polling until success or timeout), so a poll that never
  // lands leaves `poll.state` null forever. Once it gives up (`"timeout"`),
  // do one last direct fetch so the Current-plan card stops showing
  // perpetual "Loading…" when every poll tick failed.
  useEffect(() => {
    if (checkoutReturn && poll.status === "timeout" && poll.state === null) {
      refreshBillingState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutReturn, poll.status, poll.state]);

  async function upgrade(p: PlanCatalogEntry) {
    setErr(null);
    setBusy(p.provider_variant_id);
    try {
      const { url } = await api.createCheckout(p.provider_variant_id, "billing");
      window.location.href = url;
    } catch (ex) {
      setErr(String((ex as Error).message));
      setBusy(null);
    }
  }

  const ent = effectiveState?.entitlement;
  const groups = effectiveState ? groupPlansByInterval(effectiveState.plans) : [];
  // Friendly display name for the current tier — the entitlement only
  // carries the raw plan key, so borrow the catalog's display_name when
  // the tier is still in the catalog (a comp-only tier won't be).
  const currentName =
    (ent && groups.find((g) => g.plan === ent.plan)?.displayName) ??
    ent?.plan;

  return (
    <div className="main">
      <h1>Billing</h1>
      {err && <p className="error">{err}</p>}

      <div className="card">
        <h2>Current plan</h2>
        {!ent && <p className="muted">Loading…</p>}

        {ent && isLocked(ent) && (
          <p style={{ margin: 0 }}>
            <strong>No active subscription.</strong> Search and company
            profiles stay open — start a subscription below to unlock full
            financials + alerts. Any companies you tracked before are saved.
          </p>
        )}

        {ent && !isLocked(ent) && (
          <>
            <div className="plan-summary-head">
              <span className="plan-summary-name">{currentName}</span>
              <CurrentPlanStatus ent={ent} />
              {ent.billing_provider === "comp" && (
                <span className="badge">complimentary</span>
              )}
            </div>

            <ul className="plan-features">
              {planFeatures(ent).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>

            {ent.trial_ends_at && !ent.grace_until && (
              <TrialNote iso={ent.trial_ends_at} />
            )}
            {ent.grace_until && (
              <p className="error" style={{ margin: "var(--space-4) 0 0" }}>
                <strong>Payment failed.</strong> Service continues until{" "}
                {ent.grace_until.slice(0, 10)} — update your payment method
                from the customer portal linked in your Lemon Squeezy receipt
                email to avoid interruption.
              </p>
            )}
          </>
        )}
      </div>

      {checkoutReturn && poll.status !== "success" && (
        <div className="card">
          {poll.status === "polling" ? (
            <p className="muted">
              Finishing setup — confirming your subscription with Lemon
              Squeezy…
            </p>
          ) : (
            <p className="muted">
              This can take a minute. Refresh this page shortly, or contact{" "}
              <a href={`mailto:${brand.supportEmail}`}>
                {brand.supportEmail}
              </a>{" "}
              if your plan doesn't update.
            </p>
          )}
        </div>
      )}

      <div className="card">
        <h2>Plans</h2>
        {effectiveState && !effectiveState.checkout_configured && (
          <p className="muted">
            Self-serve checkout isn’t enabled yet — contact{" "}
            <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a> to
            change plan.
          </p>
        )}

        {groups.length > 0 && (
          <div
            className="seg"
            role="tablist"
            aria-label="Billing interval"
            style={{ marginBottom: "var(--space-2)" }}
          >
            <IntervalToggle value={billingInterval} onChange={setBillingInterval} />
          </div>
        )}

        {groups.length > 0 && (
          <div className="plan-grid">
            {groups.map((g) => {
              const row = rowForInterval(g, billingInterval);
              if (!row) return null;
              const savings =
                billingInterval === "year"
                  ? intervalSavingsLabel(g.month, g.year)
                  : null;
              const isCurrent = !!ent && ent.plan === g.plan;
              const canSwitch =
                !!ent && !isCurrent && !!effectiveState?.checkout_configured;
              const parts = planPriceParts(row);
              return (
                <div
                  key={g.plan}
                  className={`plan-card${isCurrent ? " current" : ""}`}
                >
                  <div className="plan-card-head">
                    <span className="plan-card-name">{g.displayName}</span>
                    {isCurrent && (
                      <span className="pill ok">
                        <span className="pill-dot" aria-hidden />
                        Current
                      </span>
                    )}
                  </div>

                  {parts && (
                    <div className="plan-card-price">
                      <span className="amount">{parts.amount}</span>
                      {parts.unit && <span className="unit">{parts.unit}</span>}
                      {/* Savings sits with the price so an annual figure reads
                          as the annual *option*, on the current card too. */}
                      {savings && <span className="save-badge">{savings}</span>}
                    </div>
                  )}

                  <ul className="plan-features">
                    {planFeatures(row).map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>

                  {/* The entitlement carries no billing interval, so the
                      current tier is matched by name only — its card can't
                      assert which interval/price the account is actually on.
                      Point to the portal (where cancel + interval changes
                      happen) rather than restating a possibly-wrong price as
                      "your current plan". */}
                  {isCurrent ? (
                    <div className="plan-cta-current">
                      Manage or cancel in the customer portal
                    </div>
                  ) : canSwitch ? (
                    <button
                      className="btn primary plan-cta"
                      disabled={busy !== null}
                      onClick={() => upgrade(row)}
                    >
                      {busy === row.provider_variant_id
                        ? "Opening…"
                        : isLocked(ent)
                          ? "Start free trial"
                          : "Switch to this plan"}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <p className="muted" style={{ margin: 0 }}>
          New plans start with a free trial. Payments are processed by Lemon
          Squeezy as merchant-of-record (VAT and sales tax handled). Cancel any
          time, or switch between monthly and annual billing, from the customer
          portal linked in your receipt email — search and company profiles stay
          open, and your tracked companies are saved.
        </p>
      </div>
    </div>
  );
}

// Status pill for the active current plan, in priority order: a payment
// failure in its grace window outranks everything, then an active trial
// countdown, otherwise a plain "Active". Mirrors BillingBanner's ordering.
function CurrentPlanStatus({ ent }: { ent: Entitlement }) {
  if (ent.grace_until) {
    return (
      <span className="pill danger">
        <span className="pill-dot" aria-hidden />
        Payment failed
      </span>
    );
  }
  if (ent.trial_ends_at) {
    const n = daysLeft(ent.trial_ends_at);
    return (
      <span className="pill warn">
        <span className="pill-dot" aria-hidden />
        {/* daysLeft clamps to 0 on the final day — don't say "0 days left". */}
        {n === 0 ? "Trial · last day" : `Trial · ${n} day${n === 1 ? "" : "s"} left`}
      </span>
    );
  }
  return (
    <span className="pill ok">
      <span className="pill-dot" aria-hidden />
      Active
    </span>
  );
}

// Trial countdown line under the current-plan facts. Computes daysLeft
// once and spells out the last-day case rather than "ends in 0 days".
function TrialNote({ iso }: { iso: string }) {
  const n = daysLeft(iso);
  return (
    <p className="muted" style={{ margin: "var(--space-4) 0 0" }}>
      {n === 0
        ? "Your free trial ends today — you’ll be billed after that unless you cancel."
        : `Your free trial ends in ${n} day${n === 1 ? "" : "s"} — you’ll be billed after that unless you cancel.`}
    </p>
  );
}
